import React, { useMemo } from 'react';
import { useRole } from '../../context/roleContext';
import {
  faFileExport,
  faTrash,
  faRefresh,
  faFileImport,
  faAdd
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';

/**
 * ProductSizeActions Component
 * Action buttons for product size management (bulk actions, export, import, etc.)
 */
const ProductSizeActions = ({
  selectedProductSizes = [],
  productSizes = [],
  onAddProductSize,
  onDeleteSelected,
  onRefresh,
  onImport,
  onExport,
  onDownloadTemplate,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  loading = false,
  refreshLoading = false,
  importing = false,
  exporting = false
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();
  /**
   * Build the toolbar actions array.
   */
  const actions = useMemo(() => {
    const items = [];

    if (selectedProductSizes.length > 0 && checkDeleteAccess()) {
      items.push({
        key: 'delete-selected',
        label: t('common.buttons.deleteSelected'),
        icon: faTrash,
        onClick: onDeleteSelected,
        disabled: loading || importing || exporting,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    items.push(
      {
        key: 'import-product-sizes',
        label: t('productSizes.actions.buttons.import'),
        icon: faFileImport,
        onClick: onImport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        spinner: importing,
      },
      {
        key: 'export-product-sizes',
        label: t('common.buttons.exportCsv'),
        icon: faFileExport,

        onClick: onExport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        textColor: UI_COLORS.TEXT.WHITE,
      },
      {
        key: 'refresh-product-sizes',
        label: t('common.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading || importing || exporting,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'add-product-size',
        label: t('productSizes.actions.buttons.add'),
        icon: faAdd,
        onClick: onAddProductSize,
        disabled: loading || importing || exporting,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      }
    );

    return items;
  }, [
    onExport,
    loading,
    onAddProductSize,
    onDeleteSelected,
    onRefresh,
    onImport,
    refreshLoading,
    selectedProductSizes.length,
    importing,
    exporting,
    checkDeleteAccess,
    t
  ]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('productSizes.actions.searchPlaceholder')}
      actions={actions}
    />
  );
};

export default ProductSizeActions;
